import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./Contact.css";
import Footer from "../components/Footer";
import { FaWhatsapp } from "react-icons/fa";
import "../styles/theme.css";

const ENDPOINT = process.env.REACT_APP_EMAIL_ENDPOINT;
const TO_EMAIL = process.env.REACT_APP_CONTACT_TO;
const FROM_NAME = process.env.REACT_APP_FROM_NAME || "CRIGO Technologies AB";
const FROM_ADDR = process.env.REACT_APP_FROM_EMAIL || "noreply@crigotechnologies.se";

const Contact = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });

  const [isRecording, setIsRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success'|'error', text: string }

  // 🎙️ Voice recognition setup
  let SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SpeechRecognition) {
    console.warn("Speech recognition is not supported in this browser.");
    SpeechRecognition = null;
  }

  const recognition = SpeechRecognition ? new SpeechRecognition() : null;
  if (recognition) {
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";

    recognition.onstart = () => setIsRecording(true);
    recognition.onend = () => setIsRecording(false);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setFormData((prev) => ({ ...prev, message: transcript }));
    };
  }

  const startRecording = () => {
    if (recognition) {
      recognition.start();
    } else {
      alert("Sorry, your browser does not support voice input.");
    }
  };

  const stopRecording = () => {
    if (recognition) {
      recognition.stop();
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 🚀 Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    // basic validation
    if (!formData.name.trim())
      return setStatus({ type: "error", text: "Please enter your name." });
    if (!formData.email.trim())
      return setStatus({ type: "error", text: "Please enter your email." });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      return setStatus({ type: "error", text: "Please enter a valid email." });
    if (!formData.message.trim())
      return setStatus({ type: "error", text: "Please enter a message." });

    // format email body
    const html = `
      <div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.6">
        <h2>New Contact Form Message</h2>
        <p><strong>Name:</strong> ${escapeHtml(formData.name)}</p>
        <p><strong>Email:</strong> ${escapeHtml(formData.email)}</p>
        ${
          formData.service
            ? `<p><strong>Service:</strong> ${escapeHtml(formData.service)}</p>`
            : ""
        }
        <p><strong>Message:</strong></p>
        <p>${escapeHtml(formData.message).replace(/\n/g, "<br/>")}</p>
      </div>
    `;

    try {
      setSending(true);
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from: `${FROM_NAME} <${FROM_ADDR}>`,
          to: TO_EMAIL,
          subject: `New message from ${formData.name}`,
          html,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      setStatus({
        type: "success",
        text: "Message sent! We’ll get back to you shortly.",
      });
      setFormData({ name: "", email: "", service: "", message: "" });
    } catch (err) {
      setStatus({
        type: "error",
        text: err.message || "Something went wrong. Please try again.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-container">
      <div className="main-content">
        {/* 🟩 Hero Section */}
        <div className="contact-hero">
          <h2>{t("contact.title")}</h2>
          <p>{t("contact.subtitle")}</p>
        </div>

        {/* 🟩 Contact Form */}
        <div className="contact-form-container">
          <form onSubmit={handleSubmit} className="contact-form">
            <input
              type="text"
              name="name"
              placeholder={t("contact.name")}
              value={formData.name}
              onChange={handleChange}
              required
            />
            <input
              type="email"
              name="email"
              placeholder={t("contact.email")}
              value={formData.email}
              onChange={handleChange}
              required
            />
            <select name="service" onChange={handleChange} value={formData.service} required>
              <option value="">{t("contact.selectService")}</option>
              <option value="Web Development">{t("contact.webDevelopment")}</option>
              <option value="Mobile App Design">{t("contact.mobileAppDesign")}</option>
              <option value="IT Consulting">{t("contact.itConsulting")}</option>
            </select>
            <textarea
              name="message"
              placeholder={t("contact.message")}
              value={formData.message}
              onChange={handleChange}
            ></textarea>

            {/* 🟩 Voice Input */}
            <div className="voice-recording">
              <p>{t("contact.preferSpeak")}</p>
              <button type="button" onClick={startRecording} className="record-btn">
                🎤 {isRecording ? t("contact.listening") : t("contact.startRecording")}
              </button>
              {isRecording && (
                <button type="button" onClick={stopRecording} className="stop-btn">
                  🛑 {t("contact.stopRecording")}
                </button>
              )}
            </div>

            {/* 🟩 Submit Button */}
            <button type="submit" className="submit-btn" disabled={sending}>
              {sending ? t("contact.sending") || "Sending..." : t("contact.submit")}
            </button>

            {/* 🟩 Feedback Message */}
            {status && (
              <p
                role="alert"
                style={{
                  marginTop: 12,
                  color: status.type === "success" ? "limegreen" : "tomato",
                }}
              >
                {status.text}
              </p>
            )}
          </form>
        </div>

        {/* 🟩 Contact Details */}
        <div className="contact-details">
          <div className="contact-box">
            📍 <strong>{t("contact.location")}:</strong> Kiruna, Sweden
          </div>
          <div className="contact-box">
            📧 <strong>{t("contact.email")}:</strong> info@crigotechnologies.se
          </div>
          <div className="contact-box">
            📞 <strong>{t("contact.phone")}:</strong> +46 73 619 3728
          </div>
        </div>

        {/* 🟩 Google Map */}
        <div className="map-container">
          <iframe
            title="Google Map"
            width="100%"
            height="300"
            frameBorder="0"
            scrolling="no"
            marginHeight="0"
            marginWidth="0"
            src="https://maps.google.com/maps?q=kiruna&t=&z=13&ie=UTF8&iwloc=&output=embed"
          ></iframe>
        </div>
      </div>

      {/* 🟩 WhatsApp Floating Button */}
      <a
        href="https://wa.me/46736193728"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-button"
      >
        <FaWhatsapp size={28} />
      </a>

      <Footer />
    </div>
  );
};

// helper to prevent HTML injection
function escapeHtml(str) {
  return String(str)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export default Contact;